import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useMutation } from "convex/react";
import { useAuth } from "@clerk/clerk-react";
import { api } from "../../api";
import { ConvexHttpClient } from "convex/browser";
import { getCurrentConvexUrl } from "@/config/deployments";

// Wildcard app ID - the "star" app that should contain ALL webkit URLs
const WILDCARD_APP_ID = "j57fgp9z6fejkw5frve4bnx1957m90gc";

export const useSyncWebkitToWildcard = () => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [progress, setProgress] = useState({ processed: 0, total: 0 });
  const { toast } = useToast();
  const { getToken } = useAuth();
  const updateWebkit = useMutation(api.apps.update.webkit);

  const syncAllToWildcard = async () => {
    setIsSyncing(true);
    setProgress({ processed: 0, total: 0 });

    try {
      const convexUrl = getCurrentConvexUrl();
      const client = new ConvexHttpClient(convexUrl);
      
      // Get Clerk auth token and set it on the client
      const token = await getToken({ template: "convex" });
      if (token) {
        client.setAuth(token);
      }
      
      const allUrls = new Set<string>();
      let cursor: string | null = null;
      let totalApps = 0;
      let appsWithUrls = 0;

      // Paginate through all apps
      do {
        const result = await client.query(api.apps.list.all, {
          paginationOpts: { cursor, numItems: 100 }
        });

        const appIds = result.page.map((app: any) => app.id);
        totalApps += appIds.length;

        // Fetch webkit URLs for this batch
        if (appIds.length > 0) {
          const appsData = await client.query(api.apps.get.apps, { apps: appIds });
          
          for (const app of appsData) {
            const webkit = (app as any)?.webkit || [];
            if (webkit.length > 0) {
              appsWithUrls++;
              for (const url of webkit) {
                allUrls.add(url.trim().toLowerCase());
              }
            }
          }
        }

        setProgress({ processed: totalApps, total: result.isDone ? totalApps : totalApps + 100 });
        cursor = result.isDone ? null : result.continueCursor;
      } while (cursor);

      // Get current wildcard URLs
      const wildcardData = await client.query(api.apps.get.apps, { apps: [WILDCARD_APP_ID as any] });
      const currentWildcardUrls = (wildcardData?.[0] as any)?.webkit || [];
      const currentSet = new Set(currentWildcardUrls);

      // Find new URLs to add
      const newUrls = Array.from(allUrls).filter(url => !currentSet.has(url));

      if (newUrls.length > 0) {
        // Update wildcard with all URLs
        const mergedUrls = [...currentWildcardUrls, ...newUrls];
        await updateWebkit({
          app: WILDCARD_APP_ID as any,
          webkit: mergedUrls
        });

        toast({
          title: "Sync Complete",
          description: `Added ${newUrls.length} new URLs to wildcard app. Total: ${mergedUrls.length} URLs from ${appsWithUrls} apps.`,
        });
      } else {
        toast({
          title: "Already Synced",
          description: `Wildcard app already has all ${allUrls.size} URLs from ${appsWithUrls} apps.`,
        });
      }

      console.log(`[Sync] Scanned ${totalApps} apps, found ${appsWithUrls} with URLs, ${allUrls.size} unique URLs, added ${newUrls.length} new`);
    } catch (error) {
      console.error('Sync failed:', error);
      toast({
        title: "Sync Failed",
        description: error instanceof Error ? error.message : "Failed to sync URLs",
        variant: "destructive",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  return {
    syncAllToWildcard,
    isSyncing,
    progress
  };
};
